import { Box, Flex, Text } from '@pancakeswap/uikit'
import { useUserSlippage } from '@pancakeswap/utils/user'
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import type { Address } from 'viem'
import NoBnbGuide, { isNoGasError } from 'components/NoBnbGuide'
import ConnectWalletButton from 'components/ConnectWalletButton'
import { DISPLAY, MONO, Panel, PrimaryCta, Segmented, SegBtn, Skel } from 'views/TokenPages/ui'
import { EXPLORER, short } from 'views/TradingCompetitionV3/config'
import { useV3Wallet } from 'views/TradingCompetitionV3/hooks'
import { PoolError } from './chain'
import { GAS_RESERVE_WEI, Side, fmtUnits, getTokenLink, isFeeOnTransfer, legsOf, liquidityLevel, parseAmount, presetsFor, shareOf } from './plan'
import { usePoolView, useTradeAccount, useTradeQuote, useTradeSender } from './usePoolTrade'

const Field = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 6px;
`
const Row = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
`
const AmountBox = styled.div<{ $bad?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 56px;
  padding: 0 14px;
  border-radius: 16px;
  background: rgba(5, 7, 19, 0.7);
  border: 1px solid ${({ theme, $bad }) => ($bad ? theme.colors.failure : theme.colors.cardBorder)};
  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(127, 212, 232, 0.16);
  }
  /* the site's global input rules add their own border, background and focus ring: start from nothing so the box above is the only frame */
  input {
    all: unset;
    box-sizing: border-box;
    flex: 1 1 0;
    min-width: 0;
    height: 52px;
    color: ${({ theme }) => theme.colors.text};
    font-family: ${MONO};
    font-size: 20px;
    &::placeholder {
      color: #8f9bb8;
    }
  }
  span {
    flex: none;
    font-weight: 800;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
`
const Chips = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
`
const Chip = styled.button`
  min-height: 44px;
  min-width: 0;
  padding: 0 4px;
  border-radius: 12px;
  cursor: pointer;
  font-family: inherit;
  font-weight: 700;
  font-size: 13px;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.text};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primary};
  }
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`
const GetLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 14px;
  cursor: pointer;
  font-family: inherit;
  font-weight: 800;
  font-size: 14px;
  text-decoration: none;
  color: #d7f3fb;
  background: rgba(127, 212, 232, 0.14);
  border: 1px solid rgba(127, 212, 232, 0.5);
  &:hover {
    background: rgba(127, 212, 232, 0.22);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`
const Quote = styled.dl`
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 12px 14px;
  border-radius: 16px;
  background: rgba(5, 7, 19, 0.45);
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  div {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    min-width: 0;
  }
  dt {
    font-size: 13px;
    color: ${({ theme }) => theme.colors.textSubtle};
    flex: none;
  }
  dd {
    margin: 0;
    text-align: right;
    font-size: 14px;
    font-weight: 700;
    overflow-wrap: anywhere;
    min-width: 0;
  }
`
const Notice = styled.div<{ $tone: 'warn' | 'bad' | 'ok' | 'info' }>`
  padding: 10px 12px;
  border-radius: 14px;
  font-size: 13px;
  line-height: 19px;
  color: ${({ theme, $tone }) => ($tone === 'bad' ? theme.colors.failure : $tone === 'warn' ? theme.colors.warning : $tone === 'ok' ? theme.colors.success : theme.colors.text)};
  background: ${({ $tone }) => ($tone === 'bad' ? 'rgba(255, 107, 122, 0.10)' : $tone === 'warn' ? 'rgba(255, 194, 74, 0.10)' : $tone === 'ok' ? 'rgba(57, 217, 138, 0.10)' : 'rgba(127, 212, 232, 0.08)')};
  border: 1px solid ${({ $tone }) => ($tone === 'bad' ? 'rgba(255, 107, 122, 0.4)' : $tone === 'warn' ? 'rgba(255, 194, 74, 0.4)' : $tone === 'ok' ? 'rgba(57, 217, 138, 0.4)' : 'rgba(127, 212, 232, 0.28)')};
  overflow-wrap: anywhere;
  a {
    color: inherit;
    text-decoration: underline;
  }
`
const Tick = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.success};
  background: rgba(57, 217, 138, 0.12);
  border: 1px solid rgba(57, 217, 138, 0.45);
`
const SLIPPAGES = [10, 50, 100, 300]
const pct = (p: { toSignificant: (n?: number) => string } | null | undefined) => (p ? `${p.toSignificant(2)}%` : '—')

export interface PoolTradePanelProps {
  /** The pool to trade in: the competition's pool, or the pool picked in the chart's picker on the token page. */
  pool?: Address | null
  /** The token being bought and sold. */
  projectToken: Address
  /** USD liquidity of the pool when the page knows it (for the thin-pool notice). */
  liquidityUsd?: number | null
  /** The pool picker on the token page: rendered above the "trading only on" line. */
  picker?: React.ReactNode
  /** Competition mode: the panel says it counts, links the rules, and is shown only while a competition is active (the page decides). */
  competition?: { id: number; rulesHref: string }
  /** Something the user must do first (the rules acknowledgement): shown instead of the form until the page passes null. */
  gate?: React.ReactNode
  /** Buy or sell can be controlled by the page (the phone header's Buy and Sell buttons). */
  side?: Side
  onSide?: (s: Side) => void
  onTraded?: () => void
}

/**
 * Trade IN the page, through exactly one pool. The quote comes from that pool's own exchange; the transaction is built for that pool, read back
 * and checked against the exchange's factory before the wallet opens (route.ts). No aggregator, no other pool, no redirect to Swap.
 */
export default function PoolTradePanel({ pool, projectToken, liquidityUsd, picker, competition, gate, side: sideProp, onSide, onTraded }: PoolTradePanelProps) {
  const [sideState, setSideState] = useState<Side>('buy')
  const side = sideProp ?? sideState
  const setSide = (s: Side) => {
    setSideState(s)
    onSide?.(s)
  }
  const [text, setText] = useState('')
  const [accept, setAccept] = useState(false)
  const [slippage, setSlippage] = useUserSlippage()
  const { address, isConnected, wrongChain, switching, switchToBsc } = useV3Wallet()
  const viewQ = usePoolView(pool)
  const view = viewQ.data
  const legs = useMemo(() => (view ? legsOf(view, projectToken, side) : null), [view, projectToken, side])
  const amountIn = useMemo(() => (legs ? parseAmount(text, legs.tokenIn.decimals) : null), [legs, text])
  const spender = view?.dex.router
  const account = useTradeAccount(isConnected ? (address as Address | undefined) : undefined, legs, spender)
  const quoteQ = useTradeQuote(view, legs, amountIn, isConnected ? (address as Address | undefined) : undefined)
  const quote = quoteQ.data
  const sender = useTradeSender()
  const now = Date.now()

  // a different pool or side is a different trade: start clean
  useEffect(() => {
    setText('')
    setAccept(false)
    sender.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool, side])
  useEffect(() => setAccept(false), [text])

  if (!pool) return <Panel data-testid="pt-panel" id="trade"><Text bold>No pool to trade in</Text><Text fontSize="14px" color="textSubtle" mt="4px">Trading opens once the token has a verified pool.</Text></Panel>
  if (viewQ.isLoading && !view) return <Panel data-testid="pt-panel" id="trade" aria-busy="true"><Skel $h={24} $w="50%" /><Skel $h={56} style={{ marginTop: 16 }} $r={16} /><Skel $h={44} style={{ marginTop: 12 }} $r={12} /></Panel>
  if (!view || !legs) {
    const code = (viewQ.error as PoolError | undefined)?.code
    return (
      <Panel data-testid="pt-panel" id="trade">
        <Text bold data-testid="pt-unavailable">{view && !legs ? 'This pool does not hold this token' : code === 'unsupported_exchange' ? 'This exchange is not supported here' : 'The pool could not be read'}</Text>
        <Text fontSize="14px" color="textSubtle" mt="4px">Nothing can be traded right now. {code === 'unsupported_exchange' ? 'Pick another pool.' : 'Try again in a moment.'}</Text>
      </Panel>
    )
  }

  const feeText = view.facts.fee !== null ? `${(view.facts.fee / 10_000).toString()}%` : `${((view.dex.v2FeeBps ?? 25) / 100).toString()}%`
  const pair = `${legs.project.symbol}/${legs.quote.symbol}`
  const poolLabel = `${view.dex.name} · ${pair}${view.facts.fee !== null ? ` ${feeText}` : ''}`
  const inDec = legs.tokenIn.decimals
  const balance = legs.nativeIn ? account.data?.bnb : account.data?.balance
  const insufficient = amountIn !== null && balance !== undefined && amountIn > balance
  const needsApproval = Boolean(amountIn && !legs.nativeIn && account.data && account.data.allowance < amountIn)
  const impactPct = quote?.impact?.withoutFee ?? null
  const impactBips = impactPct ? Number(impactPct.multiply(10_000).quotient) : 0
  const impactBlocked = impactBips >= 1500
  const impactConfirm = impactBips >= 1000 && !impactBlocked
  const impactHigh = impactBips >= 500
  const stale = quote ? now - quote.at > 30_000 : false
  const level = liquidityLevel(liquidityUsd)
  const feeToken = isFeeOnTransfer(projectToken)

  let reason: string | null = null
  if (feeToken) reason = 'This token takes a fee on transfer'
  else if (!amountIn) reason = 'Enter an amount'
  else if (insufficient) reason = `Not enough ${legs.inLabel}`
  else if (quoteQ.isLoading && !quote) reason = 'Getting a quote…'
  else if (quoteQ.error && !quote) reason = 'No quote for this amount'
  else if (quote?.routeError) reason = 'Route check failed'
  else if (stale && quoteQ.isValidating) reason = 'Refreshing the quote…'
  else if (impactBlocked) reason = 'Price impact is too high'
  else if (impactConfirm && !accept) reason = 'Accept the price impact to continue'

  const { busy } = sender
  const run = async () => {
    if (!amountIn || !quote || !address) return
    if (needsApproval) {
      if (await sender.approve(legs.tokenIn.address, view.dex.router, amountIn)) await account.mutate()
      return
    }
    const done = await sender.swap(view, legs, amountIn, quote.amountOut, slippage, address as Address, view.facts.pool)
    if (done) {
      setText('')
      await Promise.all([account.mutate(), quoteQ.mutate()])
      onTraded?.()
    }
  }

  const st = sender.status
  const busyLabel = st.step === 'approving' ? 'Approving…' : st.step === 'checking' ? 'Checking the route…' : st.step === 'wallet' ? 'Confirm in your wallet…' : st.step === 'pending' ? 'Waiting for confirmation…' : null
  const actionLabel = needsApproval ? `Approve ${legs.inLabel}` : `${side === 'buy' ? 'Buy' : 'Sell'} ${legs.project.symbol}`
  const big = { minHeight: 52, width: '100%' } as const

  const primary = !isConnected ? (
    <ConnectWalletButton style={big} data-testid="pt-connect">Connect wallet</ConnectWalletButton>
  ) : wrongChain ? (
    <PrimaryCta as="button" type="button" onClick={switchToBsc} disabled={switching} style={big} data-testid="pt-switch-chain">{switching ? 'Switching…' : 'Switch to BNB Smart Chain'}</PrimaryCta>
  ) : (
    <PrimaryCta
      as="button"
      type="button"
      onClick={run}
      disabled={busy || Boolean(reason)}
      aria-disabled={busy || Boolean(reason)}
      data-testid="pt-primary"
      data-reason={reason ?? ''}
      style={{ ...big, opacity: busy || reason ? 0.6 : 1 }}
    >
      {busyLabel ?? reason ?? actionLabel}
    </PrimaryCta>
  )

  const onPreset = (value: string) => {
    if (balance === undefined) return
    setText(shareOf(balance, Number(value), inDec, legs.nativeIn && Number(value) >= 100 ? GAS_RESERVE_WEI : 0n))
  }
  // a connected wallet that holds none of what goes in gets "Get <token>" instead of chips that would do nothing
  const empty = isConnected && balance !== undefined && balance === 0n
  const get = getTokenLink(legs)

  return (
    <Panel data-testid="pt-panel" id="trade" data-pool={view.facts.pool.toLowerCase()} data-side={side}>
      <Flex alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8 }}>
        <Text as="h3" bold style={{ fontFamily: DISPLAY, fontSize: 20, margin: 0 }}>{competition ? 'Trade in this competition' : 'Trade'}</Text>
        {competition && !quote?.routeError && <Tick data-testid="pt-counts">Counts for this competition ✓</Tick>}
      </Flex>
      {picker && <Box mt="12px">{picker}</Box>}
      <Text fontSize="14px" mt="12px" data-testid="pt-pool-label" style={{ overflowWrap: 'anywhere' }}>
        <b>Trading only on {poolLabel}</b>
      </Text>
      <Text fontSize="12px" color="textSubtle" style={{ overflowWrap: 'anywhere' }}>
        Pool <a href={`${EXPLORER}/address/${view.facts.pool}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>{short(view.facts.pool)}</a>
        {competition ? ' · a trade routed through any other pool does not count.' : ' · no other pool is used.'}
        {competition && <> <a href={competition.rulesHref} style={{ textDecoration: 'underline' }} data-testid="pt-rules-link">Competition rules</a></>}
      </Text>

      {gate ? (
        <Box mt="16px">{gate}</Box>
      ) : (
        <>
          <Segmented $cols={2} role="tablist" aria-label="Buy or sell" style={{ marginTop: 16 }}>
            <SegBtn role="tab" aria-selected={side === 'buy'} $on={side === 'buy'} $tone="up" onClick={() => setSide('buy')} data-testid="pt-side-buy">Buy</SegBtn>
            <SegBtn role="tab" aria-selected={side === 'sell'} $on={side === 'sell'} $tone="down" onClick={() => setSide('sell')} data-testid="pt-side-sell">Sell</SegBtn>
          </Segmented>

          <Field style={{ marginTop: 16 }}>
            <Row>
              <Text fontSize="13px" color="textSubtle" as="label" htmlFor="pt-amount">{side === 'buy' ? `You pay (${legs.inLabel})` : `You sell (${legs.inLabel})`}</Text>
              {isConnected && balance !== undefined && (
                <Text fontSize="12px" color="textSubtle" data-testid="pt-balance">Balance <span style={{ fontFamily: MONO }}>{fmtUnits(balance, inDec)}</span> {legs.inLabel}</Text>
              )}
            </Row>
            <AmountBox $bad={insufficient}>
              <input id="pt-amount" data-testid="pt-amount" inputMode="decimal" autoComplete="off" autoCorrect="off" spellCheck={false} placeholder="0.0" value={text} onChange={(e) => setText(e.target.value.replace(',', '.'))} aria-label={`Amount of ${legs.inLabel}`} />
              <span>{legs.inLabel}</span>
            </AmountBox>
          </Field>

          {empty ? (
            <Box mt="8px" data-testid="pt-get">
              <Text fontSize="14px" color="textSubtle">You hold no {legs.inLabel} in this wallet.</Text>
              {get ? (
                <Box mt="8px">
                  <GetLink href={get.href} {...(get.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})} data-testid="pt-get-link">{get.label}</GetLink>
                </Box>
              ) : (
                <Box mt="8px">
                  <GetLink as="button" type="button" onClick={() => setSide('buy')} data-testid="pt-get-link">Buy {legs.project.symbol} first</GetLink>
                </Box>
              )}
            </Box>
          ) : (
            <Chips style={{ marginTop: 8 }}>
              {presetsFor(legs).map((p) => (
                <Chip key={p.label} type="button" onClick={() => onPreset(p.value)} disabled={balance === undefined} title={balance === undefined ? 'Connect a wallet to use a share of your balance' : `${p.value === '100' ? 'All' : `${p.value}% of`} your ${legs.inLabel} balance${legs.nativeIn && p.value === '100' ? ' (a little BNB stays for gas)' : ''}`} data-testid={`pt-preset-${p.value}`}>{p.label}</Chip>
              ))}
            </Chips>
          )}

          <Quote style={{ marginTop: 16 }} data-testid="pt-quote" aria-live="polite">
            <div>
              <dt>You get about</dt>
              <dd data-testid="pt-out">{quote ? `${fmtUnits(quote.amountOut, legs.tokenOut.decimals)} ${legs.outLabel}` : amountIn ? '…' : '—'}</dd>
            </div>
            <div>
              <dt>Minimum received</dt>
              <dd data-testid="pt-min">{quote ? `${fmtUnits(quote.minOut, legs.tokenOut.decimals)} ${legs.outLabel}` : '—'}</dd>
            </div>
            <div>
              <dt>Price impact</dt>
              <dd data-testid="pt-impact" style={{ color: impactBips >= 1000 ? '#FF6B7A' : impactHigh ? '#FFC24A' : undefined }}>{quote ? pct(impactPct) : '—'}</dd>
            </div>
            <div>
              <dt>Pool fee</dt>
              <dd>{feeText}</dd>
            </div>
          </Quote>

          <Field style={{ marginTop: 12 }}>
            <Row>
              <Text fontSize="13px" color="textSubtle">Slippage (your setting)</Text>
              <Text fontSize="13px" data-testid="pt-slippage-value">{(slippage / 100).toString()}%</Text>
            </Row>
            <Chips>
              {SLIPPAGES.map((b) => (
                <Chip key={b} type="button" onClick={() => setSlippage(b)} aria-pressed={slippage === b} style={slippage === b ? { borderColor: '#7FD4E8', background: 'rgba(127,212,232,0.14)' } : undefined} data-testid={`pt-slip-${b}`}>{b / 100}%</Chip>
              ))}
            </Chips>
          </Field>

          <Box mt="12px" style={{ display: 'grid', gap: 8 }}>
            {quote && !quote.routeError && (
              <Notice $tone="ok" data-testid="pt-route" role="status">Route verified: one hop through this pool only ({short(view.facts.pool)}). {view.dex.name} is the only exchange used.</Notice>
            )}
            {quote?.routeError && <Notice $tone="bad" role="alert" data-testid="pt-route-refused">{quote.routeError}</Notice>}
            {feeToken && <Notice $tone="warn" data-testid="pt-fee-token">{legs.project.symbol} takes a fee on transfer, which these routers cannot handle exactly. Use the Swap page, which shows the warning for it.</Notice>}
            {level !== 'ok' && (
              <Notice $tone="warn" data-testid="pt-thin">
                {level === 'very_thin' ? 'Very thin pool' : 'Thin pool'}: about ${Math.round(liquidityUsd ?? 0).toLocaleString('en-US')} of liquidity. Even a small trade can move the price a lot.
              </Notice>
            )}
            {impactHigh && !impactBlocked && <Notice $tone="warn" data-testid="pt-impact-warning">Price impact is {pct(impactPct)}: you get a worse price than the pool currently shows. Try a smaller amount.</Notice>}
            {impactConfirm && (
              <label htmlFor="pt-accept-impact" style={{ display: 'flex', gap: 10, alignItems: 'center', minHeight: 44, fontSize: 14 }}>
                <input id="pt-accept-impact" type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} data-testid="pt-accept-impact" style={{ width: 20, height: 20 }} />
                I accept a price impact of {pct(impactPct)}
              </label>
            )}
            {impactBlocked && <Notice $tone="bad" data-testid="pt-impact-blocked">Price impact of {pct(impactPct)} is above the 15% limit. Use a smaller amount.</Notice>}
            {needsApproval && !reason && <Notice $tone="info" data-testid="pt-approval">First approve the {view.dex.name} router to spend exactly {fmtUnits(amountIn ?? 0n, inDec)} {legs.inLabel}. Then the trade.</Notice>}
            {legs.nativeIn && <Text fontSize="12px" color="textSubtle">You pay in BNB; the pool trades WBNB and the router wraps it for you. Keep a little BNB for gas.</Text>}
          </Box>

          {/* too little BNB for the network fee (plus the BNB this trade sends): the "No BNB" guide, display only */}
          {isConnected && !wrongChain && !(st.step === 'failed' && isNoGasError(st.message)) && (
            <NoBnbGuide action="trade" extraWei={legs.nativeIn && amountIn && !insufficient ? amountIn : 0n} extraLabel="the BNB you trade" testId="pt-no-bnb" style={{ marginTop: 16 }} />
          )}
          <Box mt="16px">{primary}</Box>
        </>
      )}

      <Box mt="12px" data-testid="pt-status" aria-live="polite">
        {st.step === 'pending' && (
          <Notice $tone="info" role="status">Submitted. Waiting for the network… <a href={`${EXPLORER}/tx/${st.hash}`} target="_blank" rel="noopener noreferrer" data-testid="pt-tx-link">View on BscScan</a></Notice>
        )}
        {st.step === 'done' && (
          <Notice $tone="ok" role="status" data-testid="pt-done">{st.summary}. <a href={`${EXPLORER}/tx/${st.hash}`} target="_blank" rel="noopener noreferrer" data-testid="pt-tx-link">View on BscScan</a></Notice>
        )}
        {st.step === 'failed' && isNoGasError(st.message) && <NoBnbGuide action="trade" error={st.message} testId="pt-failed-no-bnb" />}
        {st.step === 'failed' && !isNoGasError(st.message) && (
          <Notice $tone="bad" role="alert" data-testid="pt-failed">{st.message}{st.hash && <> <a href={`${EXPLORER}/tx/${st.hash}`} target="_blank" rel="noopener noreferrer">View on BscScan</a></>}</Notice>
        )}
      </Box>
    </Panel>
  )
}
